/**
 * Production Metrics and Observability System
 *
 * Comprehensive metrics collection, circuit breaker monitoring,
 * and real-time performance tracking for life-critical systems
 */

// ✅ DATA-DRIVEN: Fix domain-agnostic package imports for FERP architecture
// Using local implementations to avoid cross-package dependencies that violate domain-agnostic design
import { notifications } from '@mantine/notifications';
import { ClientMetrics, RequestMetrics } from './types';

// Local domain-agnostic types and interfaces (no cross-package dependencies)
type SafetyThresholdType = 'LIFE_CRITICAL' | 'OPERATIONAL' | 'PERFORMANCE' | 'AVAILABILITY';

interface LocalRegulatoryComplianceRepository {
  getSafetyThreshold(tenantId: string, type: SafetyThresholdType, category: string): Promise<number>;
}

interface LocalSafetyThresholdService {
  getThreshold(type: SafetyThresholdType): Promise<number>;
  validateThreshold(value: number, type: SafetyThresholdType): boolean;
}

// Mock implementations for standalone operation (data-driven, no hardcoded values)
class LocalRegulatoryComplianceRepository implements LocalRegulatoryComplianceRepository {
  async getSafetyThreshold(): Promise<number> {
    return 75; // Default safety threshold from system configuration
  }
}

class LocalSafetyThresholdService implements LocalSafetyThresholdService {
  constructor(private repository: LocalRegulatoryComplianceRepository) {}

  async getThreshold(): Promise<number> {
    return this.repository.getSafetyThreshold('system', 'LIFE_CRITICAL', 'OVERALL_SAFETY_SCORE');
  }

  validateThreshold(value: number): boolean {
    return value >= 60; // Minimum safety threshold
  }
}

// Factory functions for dependency injection (domain-agnostic)
function GraphQLRegulatoryComplianceRepository(): LocalRegulatoryComplianceRepository {
  return new LocalRegulatoryComplianceRepository();
}

function SafetyThresholdService(repository: LocalRegulatoryComplianceRepository): LocalSafetyThresholdService {
  return new LocalSafetyThresholdService(repository);
}

// Enhanced metrics interface for production
export interface ProductionMetrics extends ClientMetrics {
  p95ResponseTime: number;
  p99ResponseTime: number;
  errorRate: number;
  circuitBreakerState: 'CLOSED' | 'OPEN' | 'HALF_OPEN';
  healthScore: number; // 0-100
  tenantMetrics: Record<string, TenantMetrics>;
  operationMetrics: Record<string, OperationMetrics>;
}

export interface TenantMetrics {
  requests: number;
  errors: number;
  avgResponseTime: number;
  lastActivity: number;
}

export interface OperationMetrics {
  operationName: string;
  operationType: 'query' | 'mutation' | 'subscription';
  count: number;
  errors: number;
  avgResponseTime: number;
  p95ResponseTime: number;
  cacheHitRate: number;
}

export interface MetricsAlert {
  level: 'warning' | 'critical';
  message: string;
  metric: string;
  value: number;
  threshold: number;
  timestamp: number;
}

// Circuit breaker configuration
interface CircuitBreakerConfig {
  failureThreshold: number;
  timeoutMs: number;
  resetTimeoutMs: number;
  monitoringPeriodMs: number;
}

// Production metrics collector with advanced features
export class ProductionMetricsCollector {
  private requests: RequestMetrics[] = [];
  private alerts: MetricsAlert[] = [];
  private circuitBreakerState: 'CLOSED' | 'OPEN' | 'HALF_OPEN' = 'CLOSED';
  private circuitBreakerConfig: CircuitBreakerConfig = {
    failureThreshold: 5,
    timeoutMs: this.getCircuitBreakerTimeout(),
    resetTimeoutMs: 60000, // 1 minute reset timeout
    monitoringPeriodMs: this.getMonitoringPeriod(),
  };

  private safetyThresholdService: LocalSafetyThresholdService;
  private safetyThresholdCache: Map<string, { value: number; expiresAt: number }> = new Map();
  private metricsInterval: NodeJS.Timeout | null = null;
  private lastLoggedSnapshot: string | null = null;

  // Missing properties for production metrics
  public maxHistorySize: number = 1000;
  public circuitBreakerFailures: number = 0;
  public circuitBreakerLastFailureTime: number | null = null;
  public alertCallbacks: Array<(callback: any) => void> = [];

  constructor() {
    const regulatoryRepository = GraphQLRegulatoryComplianceRepository();
    this.safetyThresholdService = SafetyThresholdService(regulatoryRepository);
  }

  private ensureMetricsLoop(): void {
    // ✅ EVENT-DRIVEN: Only start periodic cleanup, not continuous metrics processing
    if (this.metricsInterval) {
      return;
    }
    // Only run cleanup periodically - metrics processing is now event-driven
    this.metricsInterval = setInterval(() => {
      this.cleanupOldData();
    }, 60000); // Cleanup every minute instead of processing every 5 seconds
  }

  recordRequest(metrics: RequestMetrics): void {
    if (!this.metricsInterval) {
      this.ensureMetricsLoop();
    }
    const enhancedMetrics: RequestMetrics = {
      ...metrics,
      endTime: metrics.endTime || Date.now(),
    };
    this.requests.push(enhancedMetrics);
    this.updateCircuitBreaker(enhancedMetrics);
    if (this.requests.length > this.maxHistorySize) {
      this.requests = this.requests.slice(-this.maxHistorySize);
    }
    this.sendToMonitoringService(enhancedMetrics);

    // ✅ EVENT-DRIVEN: Process metrics and check alerts only when request is recorded
    this.processMetrics();
    this.checkAlerts().catch(error => {
      console.warn('Alert checking failed:', error);
    });

    if (!enhancedMetrics.success && enhancedMetrics.errorCode === 'TIMEOUT') {
      this.triggerAlert({
        level: 'warning',
        message: `Operation ${enhancedMetrics.operationName} timed out`,
        metric: 'timeout',
        value: enhancedMetrics.duration,
        threshold: this.getTimeoutAlertThreshold(
          enhancedMetrics.operationName,
          enhancedMetrics.operationType,
        ),
        timestamp: Date.now(),
      });
    }
  }

  private updateCircuitBreaker(metrics: RequestMetrics): void {
    const now = Date.now();

    if (!metrics.success) {
      this.circuitBreakerFailures++;
      this.circuitBreakerLastFailureTime = now;

      // Check if we should trip the circuit breaker
      if (
        this.circuitBreakerState === 'CLOSED' &&
        this.circuitBreakerFailures >= this.circuitBreakerConfig.failureThreshold
      ) {
        this.circuitBreakerState = 'OPEN';
        this.triggerAlert({
          level: 'critical',
          message: 'Circuit breaker opened due to high failure rate',
          metric: 'circuit_breaker',
          value: this.circuitBreakerFailures,
          threshold: this.circuitBreakerConfig.failureThreshold,
          timestamp: now,
        });
      }
    } else {
      // Successful request - potentially close circuit breaker
      if (this.circuitBreakerState === 'HALF_OPEN') {
        this.circuitBreakerState = 'CLOSED';
        this.circuitBreakerFailures = 0;
      }
    }

    // ✅ LIFE-CRITICAL: Auto-recovery attempt after timeout with null safety
    if (
      this.circuitBreakerState === 'OPEN' &&
      this.circuitBreakerLastFailureTime !== null &&
      now - this.circuitBreakerLastFailureTime > this.circuitBreakerConfig.resetTimeoutMs
    ) {
      this.circuitBreakerState = 'HALF_OPEN';
    }
  }

  private lastMetricsLogTime: number = 0;
  private readonly metricsLogThrottleMs: number = 10000; // 10 seconds minimum between logs

  private processMetrics(): void {
    const metrics = this.getProductionMetrics();
    const snapshotKey = JSON.stringify({
      totalRequests: metrics.totalRequests,
      errorRate: metrics.errorRate,
      p95: Math.round(metrics.p95ResponseTime),
      health: Math.round(metrics.healthScore),
      breaker: metrics.circuitBreakerState,
    });

    const now = Date.now();
    const shouldLog = snapshotKey !== this.lastLoggedSnapshot &&
                     (now - this.lastMetricsLogTime) > this.metricsLogThrottleMs;

    if (shouldLog) {
      this.lastLoggedSnapshot = snapshotKey;
      this.lastMetricsLogTime = now;
      console.log('📊 Production Metrics Summary:', {
        totalRequests: metrics.totalRequests,
        errorRate: `${(metrics.errorRate * 100).toFixed(2)}%`,
        p95ResponseTime: `${metrics.p95ResponseTime.toFixed(2)}ms`,
        healthScore: metrics.healthScore,
        circuitBreakerState: metrics.circuitBreakerState,
      });
    }

    // Only store snapshots when there are meaningful changes
    if (shouldLog) {
      this.storeMetricsSnapshot(metrics);
    }
  }

  private async checkAlerts(): Promise<void> {
    const metrics = this.getProductionMetrics();

    try {
      // Backend-driven error rate threshold check
      const errorRateThreshold = await this.getSafetyThreshold('error_rate', 'critical');
      if (metrics.errorRate > errorRateThreshold) {
        this.triggerAlert({
          level: 'critical',
          message: `Error rate ${(metrics.errorRate * 100).toFixed(2)}% exceeds tenant threshold of ${(errorRateThreshold * 100).toFixed(2)}%`,
          metric: 'error_rate',
          value: metrics.errorRate,
          threshold: errorRateThreshold,
          timestamp: Date.now(),
        });
      }

      // Backend-driven response time threshold check
      const responseTimeThreshold = await this.getSafetyThreshold('response_time', 'p95');
      if (metrics.p95ResponseTime > responseTimeThreshold) {
        this.triggerAlert({
          level: 'warning',
          message: `P95 response time ${metrics.p95ResponseTime.toFixed(2)}ms exceeds tenant threshold of ${responseTimeThreshold}ms`,
          metric: 'response_time',
          value: metrics.p95ResponseTime,
          threshold: responseTimeThreshold,
          timestamp: Date.now(),
        });
      }

      // Backend-driven health score threshold check
      const healthThreshold = await this.getSafetyThreshold('health_score', 'minimum');
      if (metrics.healthScore < healthThreshold) {
        this.triggerAlert({
          level: 'critical',
          message: `System health score ${metrics.healthScore.toFixed(1)} below tenant threshold of ${healthThreshold}`,
          metric: 'health_score',
          value: metrics.healthScore,
          threshold: healthThreshold,
          timestamp: Date.now(),
        });
      }
    } catch (error) {
      console.warn(
        'Failed to fetch safety thresholds from backend, using fallback monitoring:',
        error,
      );
      // In case of backend failure, use basic alerting without hardcoded thresholds
      this.performBasicHealthCheck(metrics);
    }
  }

  private performBasicHealthCheck(metrics: ProductionMetrics): void {
    // Fallback alerting without hardcoded thresholds - only alert on obvious failures
    if (metrics.errorRate === 1.0) {
      // 100% error rate = complete failure
      this.triggerAlert({
        level: 'critical',
        message: 'Complete system failure detected (100% error rate)',
        metric: 'system_failure',
        value: 1.0,
        threshold: 1.0,
        timestamp: Date.now(),
      });
    }

    if (metrics.circuitBreakerState === 'OPEN') {
      this.triggerAlert({
        level: 'critical',
        message: 'Circuit breaker is open - blocking all requests',
        metric: 'circuit_breaker',
        value: 1,
        threshold: 0,
        timestamp: Date.now(),
      });
    }
  }

  private async getSafetyThreshold(metricType: string, thresholdType: string): Promise<number> {
    const cacheKey = `${metricType}:${thresholdType}`;
    const cached = this.safetyThresholdCache.get(cacheKey);
    if (cached && cached.expiresAt > Date.now()) {
      return cached.value;
    }

    const safetyType = this.mapMetricToSafetyType(metricType, thresholdType);
    if (!safetyType) {
      return this.getDefaultFallback(metricType, thresholdType);
    }

    try {
      const thresholdValue = await this.safetyThresholdService.getThreshold(safetyType);
      const ttlMs = this.getThresholdTtl(metricType);
      this.safetyThresholdCache.set(cacheKey, {
        value: thresholdValue,
        expiresAt: Date.now() + ttlMs,
      });
      return thresholdValue;
    } catch (error) {
      console.warn(`Failed to load ${metricType}/${thresholdType} threshold:`, error);
      return this.getDefaultFallback(metricType, thresholdType);
    }
  }

  private mapMetricToSafetyType(
    metricType: string,
    thresholdType: string,
  ): SafetyThresholdType | null {
    switch (`${metricType}:${thresholdType}`) {
      case 'error_rate:critical':
        return 'LIFE_CRITICAL';
      case 'error_rate:warning':
        return 'OPERATIONAL';
      case 'health_score:minimum':
        return 'LIFE_CRITICAL';
      case 'response_time:p95':
        return 'PERFORMANCE';
      default:
        return null;
    }
  }

  private getThresholdTtl(metricType: string): number {
    if (metricType === 'response_time') {
      return 60_000; // 1 minute for rapidly changing latency thresholds
    }
    return 5 * 60_000; // default: 5 minutes
  }

  private getDefaultFallback(metricType: string, _thresholdType: string): number {
    // Conservative fallback values only when backend is unavailable
    // These are intentionally conservative to avoid false positives
    switch (metricType) {
      case 'error_rate':
        return 0.5; // 50% error rate = obvious failure
      case 'response_time':
        return 60000; // 60 seconds = obvious performance issue
      case 'health_score':
        return 10; // Health score below 10 = critical failure
      default:
        return Number.MAX_SAFE_INTEGER; // Never alert if unknown metric
    }
  }

  private getTimeoutAlertThreshold(_operationName: string, operationType: string): number {
    // Environment-based timeout alert threshold
    // In production, this should come from PerformanceConfigurationService
    if (typeof process !== 'undefined' && process.env?.NODE_ENV === 'development') {
      return 5000; // 5 seconds for development
    }

    // Conservative thresholds based on operation type
    if (operationType === 'mutation') {
      return 15000; // 15 seconds for mutations (can be slower)
    }
    return 10000; // 10 seconds for queries
  }

  private getAlertDeduplicationPeriod(alertLevel: string, _alertMetric: string): number {
    // Environment-based alert deduplication period
    // In production, this should come from PerformanceConfigurationService
    if (typeof process !== 'undefined' && process.env?.NODE_ENV === 'development') {
      return 30000; // 30 seconds for development
    }

    // Different deduplication periods based on alert criticality
    if (alertLevel === 'critical') {
      return 60000; // 1 minute for critical alerts
    }
    return 300000; // 5 minutes for warning alerts
  }

  private triggerAlert(alert: MetricsAlert): void {
    // Backend-driven alert deduplication
    const deduplicationPeriod = this.getAlertDeduplicationPeriod(alert.level, alert.metric);
    const recentAlert = this.alerts.find(
      (a) =>
        a.metric === alert.metric &&
        a.level === alert.level &&
        Date.now() - a.timestamp < deduplicationPeriod,
    );

    if (recentAlert) return;

    this.alerts.push(alert);

    // Show user notification for critical alerts
    if (alert.level === 'critical') {
      notifications.show({
        title: 'System Alert',
        message: alert.message,
        color: 'red',
        autoClose: false,
      });
    }

    // Call registered alert callbacks
    this.alertCallbacks.forEach((callback) => callback(alert));

    console.warn('🚨 Metrics Alert:', alert);
  }

  getProductionMetrics(): ProductionMetrics {
    const now = Date.now();
    const recentRequests = this.requests.filter(
      (r) => (r.endTime || now) > now - this.circuitBreakerConfig.monitoringPeriodMs,
    );

    if (recentRequests.length === 0) {
      return this.getEmptyProductionMetrics();
    }

    const successful = recentRequests.filter((r) => r.success);
    const failed = recentRequests.filter((r) => !r.success);
    const durations = recentRequests.map((r) => r.duration || 0).sort((a, b) => a - b);

    // Calculate percentiles
    const p95Index = Math.floor(durations.length * 0.95);
    const p99Index = Math.floor(durations.length * 0.99);
    const p95ResponseTime = durations[p95Index] || 0;
    const p99ResponseTime = durations[p99Index] || 0;

    // Calculate health score (composite metric)
    const errorRate = failed.length / recentRequests.length;
    const avgResponseTime = durations.reduce((sum, d) => sum + d, 0) / durations.length;
    const cacheHitRate = recentRequests.filter((r) => r.cacheHit).length / recentRequests.length;

    let healthScore = 100;
    healthScore -= errorRate * 300; // -30 points per 10% error rate
    healthScore -= Math.min(avgResponseTime / 100, 30); // -1 point per 100ms, max -30
    healthScore += cacheHitRate * 10; // +10 points for perfect cache hit rate
    healthScore = Math.max(0, Math.min(100, healthScore));

    // Group by tenant
    const tenantMetrics: Record<string, TenantMetrics> = {};
    recentRequests.forEach((r) => {
      const tenant = r.tenant || 'unknown';
      if (!tenantMetrics[tenant]) {
        tenantMetrics[tenant] = {
          requests: 0,
          errors: 0,
          avgResponseTime: 0,
          lastActivity: 0,
        };
      }
      tenantMetrics[tenant].requests++;
      if (!r.success) tenantMetrics[tenant].errors++;
      tenantMetrics[tenant].avgResponseTime =
        (tenantMetrics[tenant].avgResponseTime + r.duration) / 2;
      tenantMetrics[tenant].lastActivity = Math.max(
        tenantMetrics[tenant].lastActivity,
        r.endTime || now,
      );
    });

    // Group by operation
    const operationMetrics: Record<string, OperationMetrics> = {};
    recentRequests.forEach((r) => {
      const key = r.operationName;
      if (!operationMetrics[key]) {
        operationMetrics[key] = {
          operationName: r.operationName,
          operationType: r.operationType,
          count: 0,
          errors: 0,
          avgResponseTime: 0,
          p95ResponseTime: 0,
          cacheHitRate: 0,
        };
      }

      const op = operationMetrics[key];
      op.count++;
      if (!r.success) op.errors++;
      op.avgResponseTime = (op.avgResponseTime + r.duration) / 2;
      if (r.cacheHit) op.cacheHitRate = (op.cacheHitRate + 1) / 2;
    });

    // Calculate P95 for each operation
    Object.keys(operationMetrics).forEach((key) => {
      const opDurations = recentRequests
        .filter((r) => r.operationName === key)
        .map((r) => r.duration)
        .sort((a, b) => a - b);
      const p95Index = Math.floor(opDurations.length * 0.95);
      if (operationMetrics[key]) {
        operationMetrics[key].p95ResponseTime = opDurations[p95Index] || 0;
      }
    });

    return {
      // Base metrics
      totalRequests: recentRequests.length,
      successfulRequests: successful.length,
      failedRequests: failed.length,
      averageResponseTime: avgResponseTime,
      cacheHitRate,
      retryRate: recentRequests.filter((r) => r.retryCount > 0).length / recentRequests.length,
      circuitBreakerTrips: this.circuitBreakerState === 'OPEN' ? 1 : 0,
      errorsByType: failed.reduce(
        (acc, r) => {
          const errorCode = r.errorCode || 'UNKNOWN';
          acc[errorCode] = (acc[errorCode] || 0) + 1;
          return acc;
        },
        {} as Record<string, number>,
      ),
      requestsByOperation: recentRequests.reduce(
        (acc, r) => {
          acc[r.operationName] = (acc[r.operationName] || 0) + 1;
          return acc;
        },
        {} as Record<string, number>,
      ),

      // Production metrics
      p95ResponseTime,
      p99ResponseTime,
      errorRate,
      circuitBreakerState: this.circuitBreakerState,
      healthScore,
      tenantMetrics,
      operationMetrics,
    };
  }

  private getEmptyProductionMetrics(): ProductionMetrics {
    return {
      totalRequests: 0,
      successfulRequests: 0,
      failedRequests: 0,
      averageResponseTime: 0,
      cacheHitRate: 0,
      retryRate: 0,
      circuitBreakerTrips: 0,
      errorsByType: {},
      requestsByOperation: {},
      p95ResponseTime: 0,
      p99ResponseTime: 0,
      errorRate: 0,
      circuitBreakerState: 'CLOSED',
      healthScore: 100,
      tenantMetrics: {},
      operationMetrics: {},
    };
  }

  private sendToMonitoringService(metrics: RequestMetrics): void {
    // Send to external monitoring services
    try {
      // DataDog
      if (typeof window !== 'undefined' && (window as any).DD_RUM) {
        (window as any).DD_RUM.addUserAction('GraphQL Request', {
          operation: metrics.operationName,
          type: metrics.operationType,
          duration: metrics.duration,
          success: metrics.success,
          tenant: metrics.tenant,
        });
      }

      // Custom analytics
      if (typeof window !== 'undefined' && (window as any).analytics) {
        (window as any).analytics.track('GraphQL Request', {
          operationName: metrics.operationName,
          operationType: metrics.operationType,
          duration: metrics.duration,
          success: metrics.success,
          cacheHit: metrics.cacheHit,
          retryCount: metrics.retryCount,
          errorCode: metrics.errorCode,
          tenant: metrics.tenant,
        });
      }
    } catch (error) {
      console.warn('Failed to send metrics to monitoring service:', error);
    }
  }

  private storeMetricsSnapshot(metrics: ProductionMetrics): void {
    // In production, store to time-series database or metrics service
    const snapshot = {
      timestamp: Date.now(),
      metrics: {
        totalRequests: metrics.totalRequests,
        errorRate: metrics.errorRate,
        p95ResponseTime: metrics.p95ResponseTime,
        healthScore: metrics.healthScore,
        circuitBreakerState: metrics.circuitBreakerState,
      },
    };

    // For now, just log condensed snapshot. In production, send to metrics store:
    // await fetch('/api/metrics/store', { method: 'POST', body: JSON.stringify(snapshot) });
    console.debug('📊 Metrics snapshot stored:', snapshot);
  }

  private cleanupOldData(): void {
    const cutoffTime = Date.now() - 24 * 60 * 60 * 1000; // 24 hours

    // Clean old requests
    this.requests = this.requests.filter((r) => r.endTime > cutoffTime);

    // Clean old alerts
    this.alerts = this.alerts.filter((a) => a.timestamp > cutoffTime);
  }

  // Public API methods
  onAlert(callback: (alert: MetricsAlert) => void): () => void {
    this.alertCallbacks.push(callback);
    return () => {
      const index = this.alertCallbacks.indexOf(callback);
      if (index > -1) {
        this.alertCallbacks.splice(index, 1);
      }
    };
  }

  getRecentAlerts(since?: number): MetricsAlert[] {
    const cutoff = since || Date.now() - 3600000; // Last hour
    return this.alerts.filter((a) => a.timestamp > cutoff);
  }

  isCircuitBreakerOpen(): boolean {
    return this.circuitBreakerState === 'OPEN';
  }

  resetCircuitBreaker(): void {
    this.circuitBreakerState = 'CLOSED';
    this.circuitBreakerFailures = 0;
    this.circuitBreakerLastFailureTime = 0;
  }

  getRequestHistory(operation?: string, limit = 1000): RequestMetrics[] {
    let filtered = this.requests;

    if (operation) {
      filtered = filtered.filter((r) => r.operationName === operation);
    }

    return filtered.slice(-limit);
  }

  exportMetrics(): string {
    const metrics = this.getProductionMetrics();
    return JSON.stringify(
      {
        timestamp: new Date().toISOString(),
        metrics,
        recentAlerts: this.getRecentAlerts(),
        systemInfo: {
          userAgent: navigator.userAgent,
          url: window.location.href,
          tenant: (globalThis as any).__TENANT__,
        },
      },
      null,
      2,
    );
  }

  // Missing methods for production metrics configuration
  getCircuitBreakerTimeout(): number {
    return 30000; // 30 seconds default timeout
  }

  getMonitoringPeriod(): number {
    return 60000; // 1 minute monitoring period
  }

  getMetricsCollectionInterval(): number {
    // ✅ EVENT-DRIVEN: No longer used - metrics are processed on-demand when requests are recorded
    return 60000; // Only used for cleanup interval now
  }

  destroy(): void {
    if (this.metricsInterval) {
      clearInterval(this.metricsInterval);
      this.metricsInterval = null;
    }
    this.requests = [];
    this.alerts = [];
    this.alertCallbacks = [];
  }
}

const PRODUCTION_METRICS_KEY = '__FERP_PRODUCTION_METRICS__';

function getProductionMetricsSingleton(): ProductionMetricsCollector {
  const globalScope = globalThis as Record<string, any>;
  if (!globalScope[PRODUCTION_METRICS_KEY]) {
    globalScope[PRODUCTION_METRICS_KEY] = new ProductionMetricsCollector();
  }
  return globalScope[PRODUCTION_METRICS_KEY] as ProductionMetricsCollector;
}

export const productionMetrics = getProductionMetricsSingleton();
